/**
 * `#/listing/<id>` for a listing that is not on the floor — open the REAL
 * listing page instead of dropping the visitor on the home feed.
 *
 * THE DEFECT, and it is one root under at least seven controls. Blake's router
 * resolves the listing route ONLY against his LISTINGS array:
 *
 *     m = h.match(/^#\/listing\/(.+)$/);
 *     if (m) { const l = LISTINGS.find((x) => x.id === m[1]); if (l) return { view: "detail", l }; }
 *
 * (app.js:3701). A miss does not error and does not say anything — it falls
 * through every remaining branch to `return { view: "home" }` (app.js:3728).
 * The visitor taps their own item and lands on the feed.
 *
 * 🔴 WHY THE MISS IS ROUTINE RATHER THAN EXOTIC — the two halves disagree by
 * construction, and each half is right on its own:
 *
 *   · LISTINGS only ever holds ACTIVE rows. installFloorListings fills it from
 *     GET /api/listings, whose query carries its own `eq(listings.status,
 *     "active")` (api-server/src/routes/listings.ts:403).
 *   · the ids we hand it come from collections that span every status.
 *     mapMyCollection carries `lid` for all six statuses COLLECTION_STATUS
 *     maps (active, pending, sold, traded, unlisted, under_review), and
 *     mapNotification sets `out.lid = row.relatedListingId` with no status
 *     test at all.
 *
 * So five of the six collection statuses cannot resolve, and the commonest
 * transition makes it worse rather than better: accepting an offer locks the
 * listing to `pending` (lib/listingDisposition.lockListingForTrade), which is
 * exactly what floorTransport's own mapDealItems block already says — "the
 * moment a deal is accepted, its listing leaves LISTINGS". Nobody had drawn the
 * navigation conclusion from it. Notifications are the sharpest case of all,
 * because they mostly fire BECAUSE a listing stopped being active.
 *
 * THE SEVEN SITES, all signed-in, none of them previously covered:
 *   app.js:3941  [data-adgo]        activity drawer → NOTIFICATIONS[i].lid
 *   app.js:4406  [data-closetitem]  your collection → MY_COLLECTION[].lid
 *   app.js:4420  [data-mgract]      manager card "view" → MY_COLLECTION[].lid
 *   app.js:3044  [data-golistmsg]   an item card inside a message thread
 *   app.js:279   [data-golisting]   deal item slot
 *   app.js:319   [data-golisting]   deal item pop-out row
 *   app.js:1403  [data-golisting]   public profile manager card
 *   app.js:1846  [data-golisting]   deal ledger row
 *
 * 🔑 THIS RESTORES HIS OWN INVARIANT RATHER THAN IMPOSING OURS, and two pairs
 * settle it. app.js guards a lid against LISTINGS in TEN places before using it
 * — every one of them a RENDER. The navigations are the only readers that skip
 * the check, and in both of these the checking code and the unchecked code sit
 * in the same function:
 *
 *   · the `[data-mgract]` handler navigates on `act === "view" && m.lid`
 *     (app.js:4420) — then thirteen lines later its `edit` branch writes
 *     `const l = m.lid && LISTINGS.find((x) => x.id === m.lid)` (app.js:4433)
 *     and branches on whether it found one.
 *   · the activity drawer paints each row's thumbnail behind
 *     `a.lid && LISTINGS.find(...)` (app.js:3540), correctly showing a bell
 *     glyph instead when the listing is not on the floor. The click handler for
 *     that same row (app.js:3941) then navigates to `#/listing/<a.lid>` with no
 *     check at all. A row that has VISIBLY admitted it cannot find the listing
 *     still sends the visitor to it.
 *
 * WHY THIS GUARDS THE HASH AND NOT THE CONTROLS. listingEntryRedirect hooks
 * clicks precisely because a hash guard "cannot tell create from edit" — two
 * intents share `#/list`. `#/listing/<id>` has exactly one intent: show me this
 * listing. Guarding the destination therefore covers all eight sites above with
 * one hook and covers the ninth nobody has found yet, and it does not need to
 * read MY_COLLECTION or NOTIFICATIONS by name (three of the sites carry a
 * COLLECTION id or an array INDEX in their attribute, not the listing id, so a
 * click hook would have needed two more bridge accessors to resolve them).
 *
 * WHERE IT SENDS THEM, and why this is not a consolation prize. GET
 * /api/listings/:id (listings.ts:833) does NOT filter on status — it serves any
 * row except a `removed` one, with the owner/admin exemptions spelled out
 * there. So `/listing/<id>` (App.tsx:422 → pages/listing-landing.tsx) renders
 * the visitor's ACTUAL item for all five statuses his demo cannot: pending,
 * sold, traded, unlisted, under_review. Its CTAs are `getAppRedirect("listing")`
 * store links, not transactional buttons, so nothing on it can lie about a sold
 * item being purchasable. A `removed` listing 404s into that page's own honest
 * "This listing may have been removed or is no longer available." WE INVENT NO
 * COPY here either — every word the visitor reads is already shipped.
 *
 * 🪤 NULL MEANS "CANNOT SAY", AND IT IS LOAD-BEARING. `__gbzHasListing` returns
 * null while LISTINGS is empty, and this guard treats null as leave-it-alone.
 * The floor arrives from a fetch: between app.js's first render and
 * installFloorListings resolving, EVERY id looks absent, and a boolean would
 * make us redirect perfectly good active listings off the demo during that
 * window. Same posture as __gbzListingPrice's own null ("the row is gone",
 * treated as nothing-to-paint rather than as a zero). The cost is that a
 * genuinely empty floor keeps the old bounce, which is the one case where the
 * demo has nothing to show anyway.
 *
 * 🪤 CAPTURE PHASE, AND stopImmediatePropagation, for the reason WebInstall's
 * cross-route guard already documents (WebInstall.tsx:231): `hashchange` is
 * dispatched AT window, where capture and bubble listeners alike run in
 * registration order, and app.js's own `window.addEventListener("hashchange",
 * render)` (app.js:5344) would otherwise paint the home feed before we
 * navigate. stopImmediatePropagation is what keeps that frame from ever being
 * drawn — the exception to writeGuard's "never stopImmediatePropagation" rule,
 * which is about the CLICK path where we own other document capture listeners.
 * Our other hashchange listeners (floorTransport's maybeFetch, followUser's
 * checkInitialStatus, collectionRemove's landing) are all non-capture, so this
 * stop holds every one of them off a listing hash the page does not hold as
 * well; collectionRemove's landing is #/profile, which this never stops.
 *
 * ⚠️ MUST BE REGISTERED AFTER the cross-route guard, never before. That guard
 * calls stopImmediatePropagation on every hashchange while the visitor is off
 * /web, and it has to win: a stale listing hash changing on /help must navigate
 * nobody anywhere.
 */

/* 🪤 mapListingRow is imported rather than injected, and the distinction has a
   history: five test files factory-mock this module's source (floorTransport)
   and their factories provide only three exports. Vitest's mock proxy throws on
   the first property READ, not at import — so this binding is safe precisely
   because it is only dereferenced inside the fetch callback below, which those
   suites never reach. Do not hoist a read of it to module scope. */
import { mapListingRow } from "./floorTransport";
import type { ApiListingRow, DemoListing, DemoUser } from "./floorTransport";

/** His route pattern, verbatim in shape from app.js:3699. */
export const LISTING_HASH = /^#\/listing\/(.+)$/;

/** The real page's path (App.tsx:422). */
export const REAL_LISTING_PATH = "/listing/";

interface FallbackWindow extends Window {
  /** Bridge accessor — true/false, or null while the floor has not arrived. */
  __gbzHasListing?: (id: string) => boolean | null;
  /** installRealDiscoverSearch's merge target, reused here. Optional because an
   *  older bridge may not define it, in which case we fall back to the page
   *  that needs no bridge at all. */
  __gbzMergeListing?: (listing: DemoListing, user: DemoUser) => void;
  /** Blake's own render, so route() runs again once LISTINGS holds the row. */
  render?: () => void;
}

/**
 * The whole decision, in one place, so the listener and the one-shot check
 * below cannot drift apart. Returns the real page's URL, or null to leave the
 * demo alone.
 */
export function listingFallbackUrl(win: Window): string | null {
  const m = LISTING_HASH.exec(win.location.hash);
  if (!m) return null;
  const id = m[1];

  const hasListing = (win as FallbackWindow).__gbzHasListing;
  // Bridge not injected yet (or this is not a booted /web): his demo is the
  // only thing running, and it should stay that way.
  if (typeof hasListing !== "function") return null;

  const known = hasListing(id);
  // null = the floor has not arrived, so absence proves nothing.
  // true = his detail view works and is richer than the landing page.
  if (known !== false) return null;

  return REAL_LISTING_PATH + encodeURIComponent(id);
}

/** Real navigation, not a hash change — this deliberately leaves the SPA. */
const defaultNavigate = (win: Window) => (url: string) => win.location.assign(url);

/**
 * 🔴 REVERSED 2026-08-28. The first version had the direction wrong.
 *
 * It NAVIGATED to /listing/:id whenever the marketplace could not resolve a
 * listing. That page is real and honest, so the redirect "worked" — but it
 * ejected a signed-in visitor out of the marketplace into a separate
 * server-rendered page just for tapping their own sold item. The owner reported
 * that class of thing and was right: /listing/:id exists for CRAWLERS AND
 * PASTED LINKS, which cannot read a hash route. It is not an in-app destination.
 *
 * 🔑 THE MECHANISM TO DO IT PROPERLY ALREADY EXISTED, and I walked past it.
 * `__gbzMergeListing` is exactly this — installRealDiscoverSearch uses it to
 * merge a search hit the floor did not pre-load into LISTINGS so
 * `#/listing/<id>` resolves in place. A sold or pending listing is the same
 * problem (LISTINGS only ever holds ACTIVE rows) and deserves the same answer.
 *
 * So: FETCH, MERGE, RENDER IN PLACE. The visitor stays in the marketplace and
 * sees their item on his own detail view.
 *
 * 🪤 THE SHAPES MATCH BY CONSTRUCTION, which is what makes this cheap. Both
 * GET /api/listings (listings.ts:509/525) and GET /api/listings/:id
 * (listings.ts:893) project through the SAME `toPublicListing(row.listing)`, so
 * the by-id response is the `{ listing, seller }` shape `mapListingRow` already
 * consumes for the floor. No second mapper and no second gap log.
 *
 * THE REDIRECT SURVIVES AS THE FAILURE ARM, and only there. A non-200 means the
 * row is genuinely gone or `removed`, and /listing/:id answers that with its own
 * shipped "This listing may have been removed or is no longer available."
 */
export function installListingRouteFallback(
  win: Window,
  authFetch: (input: RequestInfo | URL, init?: RequestInit) => Promise<Response>,
  /** Seam for tests. */
  navigate: (url: string) => void = defaultNavigate(win),
): () => void {
  /** Ids already attempted, so a repaint storm cannot re-fetch the same row. */
  const attempted = new Set<string>();

  const onHashChange = (event: Event): void => {
    const url = listingFallbackUrl(win);
    if (url === null) return;

    const m = LISTING_HASH.exec(win.location.hash);
    if (!m) return;
    const id = m[1];

    /* Stop HIS render either way. It would paint the home feed — the bounce
       this module exists to prevent — and it would do so long before the fetch
       below could answer. */
    event.stopImmediatePropagation();

    if (attempted.has(id)) {
      // Tried once already this session and it did not resolve; do not loop.
      navigate(url);
      return;
    }
    attempted.add(id);

    void authFetch(`/api/listings/${encodeURIComponent(id)}`)
      .then(async (res) => {
        if (!res.ok) {
          navigate(url);
          return;
        }
        const merge = (win as FallbackWindow).__gbzMergeListing;
        if (typeof merge !== "function") {
          // Older bridge: fall back to the page that needs no bridge at all.
          navigate(url);
          return;
        }
        const { listing, user } = mapListingRow((await res.json()) as ApiListingRow);
        merge(listing, user);
        /* Repaint so route() runs again — it resolves now, because LISTINGS
           holds the row. render(), not rerenderKeepScroll: this IS a route
           change, and the visitor expects the top of a new view. */
        (win as FallbackWindow).render?.();
      })
      .catch(() => navigate(url));
  };

  win.addEventListener("hashchange", onHashChange, true);
  return () => win.removeEventListener("hashchange", onHashChange, true);
}

/**
 * The DEEP-LINK half, and it needs its own call rather than falling out of the
 * listener above: a link shared as `/web#/listing/<id>` sets the hash before
 * anything of ours runs, so no hashchange ever fires for it. At install time
 * the floor has not arrived and `listingFallbackUrl` correctly declines to
 * answer — so the one moment this CAN be decided is straight after
 * installFloorListings resolves, which is where WebInstall calls it. Sharing a
 * listing link is the entire reason pages/listing-landing.tsx exists, so a
 * shared link for a sold item landing on the home feed is the same defect
 * arriving by the front door.
 */
export function checkListingRouteNow(
  win: Window,
  navigate: (url: string) => void = defaultNavigate(win),
): boolean {
  const url = listingFallbackUrl(win);
  if (url === null) return false;
  navigate(url);
  return true;
}
